<div class="p-6 flex-table-page" data-testid="goods-shop-page">
  <!-- Header -->
  <div class="flex justify-between items-center mb-6 flex-table-header">
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ "goodsShop.management.title" | translate: { shopName: shopName() } }}
    </h2>
    <div class="flex items-center gap-2">
      <p-button
        data-testid="goods-shop-cancel-btn"
        label="Cancel"
        icon="pi pi-arrow-left"
        severity="secondary"
        outlined="true"
        (onClick)="onCancel()"
        styleClass="text-sm"
      ></p-button>
      <p-button
        data-testid="goods-shop-add-product-btn"
        label="Add Product"
        icon="add"
        severity="primary"
        (onClick)="openAddDialog()"
        [disabled]="!shopId()"
      ></p-button>
    </div>
  </div>

  <!-- Data Table -->
  <div class="flex-table-section">
    <app-data-table
      data-testid="goods-shop-table"
      [dataSource]="tableData()"
      [columns]="tableColumns"
      [loading]="loading()"
      [totalRecords]="totalRecords()"
      [rows]="pageSize()"
      [first]="(currentPage() - 1) * pageSize()"
      [hasCheckColumn]="false"
      scrollHeight="flex"
      (pageChange)="onPageChange($event)"
    >
    <!-- Custom Status Template -->
    <ng-template #statusTemplate let-status>
      <span
        class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
        [class]="
          status === 1
            ? 'bg-green-100 text-green-800 border-2 border-green-300'
            : 'bg-red-100 text-red-800 border-2 border-red-300'
        "
      >
        {{ status }}
      </span>
    </ng-template>

    <!-- Custom Date Template -->
    <ng-template #customTemplate let-date>
      <span class="text-sm text-gray-600">
        {{ formatDateTime(date) }}
      </span>
    </ng-template>

    <!-- Custom Actions Template -->
    <ng-template #actionsTemplate let-rowData>
      <div class="flex justify-center">
        <app-button
          [testId]="'goods-shop-action-delete'"
          class="action-delete"
          label="Delete"
          type="action"
          (emitOnClick)="handleDelete(rowData)"
        ></app-button>
      </div>
    </ng-template>
    </app-data-table>
  </div>

  <!-- Add/Edit Product Dialog -->
  <p-dialog
    data-testid="goods-shop-add-product-dialog"
    [visible]="showAddDialog()"
    [modal]="true"
    [style]="{ width: '95vw', maxWidth: '1400px' }"
    [draggable]="false"
    [resizable]="false"
    [closable]="false"
  >
    <ng-template pTemplate="header">
      <div class="flex items-center justify-between w-full">
        <h2 class="text-xl font-semibold text-gray-800 m-0">
          Select Standard Product
        </h2>
        <button
          data-testid="goods-shop-close-dialog-btn"
          type="button"
          class="custom-close-btn"
          (click)="closeAddDialog()"
          aria-label="Close dialog"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>
    </ng-template>

    <div class="space-y-6 px-6 py-4">
      <!-- Search and Filter Section -->
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label for="productCode" class="text-sm font-medium text-gray-700"
              >Product Code</label
            >
            <input
              data-testid="goods-shop-dialog-product-code-input"
              id="productCode"
              pInputText
              [(ngModel)]="productSearchForm.productCode"
              placeholder="Enter product code"
              class="w-full"
            />
          </div>
          <div class="space-y-2">
            <label for="productName" class="text-sm font-medium text-gray-700"
              >Product Name</label
            >
            <input
              data-testid="goods-shop-dialog-product-name-input"
              id="productName"
              pInputText
              [(ngModel)]="productSearchForm.productName"
              placeholder="Enter product name"
              class="w-full"
            />
          </div>
        </div>
        <div class="flex gap-2 mt-4">
          <p-button
            data-testid="goods-shop-dialog-search-btn"
            label="Search"
            severity="primary"
            size="small"
            (onClick)="onProductSearch()"
            styleClass="text-sm px-4 py-2"
          />
          <p-button
            data-testid="goods-shop-dialog-reset-btn"
            label="Reset"
            severity="secondary"
            outlined="true"
            size="small"
            (onClick)="onProductSearchReset()"
            styleClass="text-sm px-4 py-2"
          />
        </div>
      </div>

      <!-- Products Table -->
      <app-data-table
        data-testid="goods-shop-dialog-product-table"
        [dataSource]="filteredProductOptions()"
        [columns]="productTableColumns()"
        [loading]="productTableLoading()"
        [hasCheckColumn]="true"
        [hasHeaderCheckbox]="false"
        [rows]="productPageSize()"
        [first]="(productCurrentPage() - 1) * productPageSize()"
        [totalRecords]="productTotalRecords()"
        [selection]="
          selectedProductForTable() ? [selectedProductForTable()] : []
        "
        (selectionChange)="onProductTableSelectionChange($event)"
        (pageChange)="onProductPageChange($event)"
      >
      </app-data-table>
    </div>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <p-button
          data-testid="goods-shop-dialog-cancel-btn"
          label="Cancel"
          severity="secondary"
          outlined="true"
          size="small"
          (onClick)="closeAddDialog()"
          [disabled]="dialogLoading()"
          styleClass="text-sm px-4 py-2"
        />
        <p-button
          data-testid="goods-shop-dialog-confirm-btn"
          label="Confirm"
          severity="primary"
          size="small"
          (onClick)="onProductSelectionConfirm()"
          [disabled]="!selectedProductForTable() || dialogLoading()"
          [loading]="dialogLoading()"
          styleClass="text-sm px-4 py-2"
        />
      </div>
    </ng-template>
  </p-dialog>

  <!-- Toast Messages -->
  <p-toast></p-toast>

  <!-- Confirmation Dialog -->
  <p-confirmDialog></p-confirmDialog>
</div>
